import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {   
  Clock, 
  Hash, 
  Code, 
  Zap, 
  Trophy,
  Play,
  Keyboard,
  ChevronRight
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { motion, Variants } from 'framer-motion';
import { typingTestAPI } from '../utils/apiClient';
import { UserActivity, UserStats } from '../types';

interface QuickStatsProps {
  stats: UserStats | undefined;
  containerVariants: Variants;
  itemVariants: Variants;
}

const QuickStats: React.FC<QuickStatsProps> = ({ stats, containerVariants, itemVariants }) => (
  <motion.div 
    className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    <motion.div className="bg-cardBg rounded-lg p-4 text-center shadow-md" variants={itemVariants}>
      <div className="text-2xl font-bold text-accent">{stats?.avgWpm || 0}</div>
      <div className="text-sm text-textMuted">Avg WPM</div>
    </motion.div>
    <motion.div className="bg-cardBg rounded-lg p-4 text-center shadow-md" variants={itemVariants}>
      <div className="text-2xl font-bold text-accent">{stats?.bestWpm || 0}</div>
      <div className="text-sm text-textMuted">Best WPM</div>
    </motion.div>
    <motion.div className="bg-cardBg rounded-lg p-4 text-center shadow-md" variants={itemVariants}>
      <div className="text-2xl font-bold text-accent">{stats?.avgAccuracy.toFixed(1) || 0}%</div>
      <div className="text-sm text-textMuted">Accuracy</div>
    </motion.div>
    <motion.div className="bg-cardBg rounded-lg p-4 text-center shadow-md" variants={itemVariants}>
      <div className="text-2xl font-bold text-accent">{stats?.totalTests || 0}</div>
      <div className="text-sm text-textMuted">Tests</div>
    </motion.div>
  </motion.div>
);

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { state: userState } = useUser();
  const [selectedMode, setSelectedMode] = useState<'time' | 'words' | 'code'>('time');
  const [selectedDuration, setSelectedDuration] = useState(30);
  const [selectedWordCount, setSelectedWordCount] = useState(25);
  const [recentActivity, setRecentActivity] = useState<UserActivity[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3
      }
    }
  };

  // Fetch recent activity when user is authenticated
  React.useEffect(() => {
    if (userState.isAuthenticated && userState.user?.id) {
      setLoadingActivity(true);
      typingTestAPI.getRecentActivity(userState.user.id, 3)
        .then(setRecentActivity)
        .catch((error) => {
          console.error('Error fetching recent activity:', error);
          setRecentActivity([]);
        })
        .finally(() => setLoadingActivity(false));
    }
  }, [userState.isAuthenticated, userState.user?.id]);

  const modes = [
    { id: 'time', name: 'Time', icon: Clock, description: 'Type for a set amount of time' },
    { id: 'words', name: 'Words', icon: Hash, description: 'Type a specific number of words' },
    { id: 'code', name: 'Code', icon: Code, description: 'Practice typing code snippets' },
  ] as const;

  const timeOptions = [15, 30, 60, 120];
  const wordOptions = [10, 25, 50, 100];

  const handleStartTest = () => {
    navigate('/typing', { 
      state: { 
        mode: selectedMode, 
        duration: selectedDuration,
        wordCount: selectedWordCount
      } 
    });
  };

  const QuickStatsWrapper = () => (
    <QuickStats stats={userState.user?.stats} containerVariants={containerVariants} itemVariants={itemVariants} />
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <motion.div 
        className="text-center mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-4xl md:text-6xl font-bold mb-4 flex justify-center items-center">
          <Keyboard className="w-10 h-10 mr-3 text-accent hidden md:block" />
          <span className="text-accent">Type</span>
          <span className="text-text">Rush</span>
        </h1>
        <p className="text-xl text-textMuted">
          Improve your typing speed and accuracy
        </p>
      </motion.div>

      {/* Quick Stats */}
{userState.isAuthenticated && <QuickStatsWrapper />}

      {/* Mode Selection */}
      <motion.div 
        className="bg-cardBg rounded-xl p-6 mb-6 shadow-lg border border-secondary"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Select Your Typing Test</h2>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6 max-w-2xl mx-auto">
          {modes.map((mode) => {
            const Icon = mode.icon;
            return (
              <button
                key={mode.id}
                onClick={() => setSelectedMode(mode.id)}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                  selectedMode === mode.id
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-secondary hover:border-accent/50 text-textMuted hover:text-text'
                }`}
              >
                <Icon className="w-6 h-6 mx-auto mb-2" />
                <div className="font-medium">{mode.name}</div>
              </button>
            );
          })}
        </div>



        {/* Mode Options */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {selectedMode === 'time' && timeOptions.map((time) => (
            <button
              key={time}
              onClick={() => setSelectedDuration(time)}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                selectedDuration === time
                  ? 'bg-accent text-primary font-medium'
                  : 'bg-secondary text-text hover:bg-accent/20'
              }`}
            >
              {time}s
            </button>
          ))}
          
          {selectedMode === 'words' && wordOptions.map((count) => (
            <button
              key={count}
              onClick={() => setSelectedWordCount(count)}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                selectedWordCount === count
                  ? 'bg-accent text-primary font-medium'
                  : 'bg-secondary text-text hover:bg-accent/20'
              }`}
            >
              {count} words
            </button>
          ))}
        </div>

        {/* Start Button */}
        <div className="text-center">
          <motion.button
            onClick={handleStartTest}
            className="bg-accent hover:bg-yellow-500 text-primary font-semibold px-8 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center space-x-2 mx-auto"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Play className="w-5 h-5" />
            <span>Start Typing</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <motion.button
          onClick={() => navigate('/battle')}
          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white p-6 rounded-xl transition-all duration-200 shadow-lg"
          whileHover={{ scale: 1.03, y: -5 }}
          whileTap={{ scale: 0.97 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-1">1v1 Battle</h3>
              <p className="text-sm opacity-90">Challenge other typers</p>
            </div>
            <Zap className="w-8 h-8" />
          </div>
        </motion.button>

        <motion.button
          onClick={() => navigate('/leaderboard')}
          className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white p-6 rounded-xl transition-all duration-200 shadow-lg"
          whileHover={{ scale: 1.03, y: -5 }}
          whileTap={{ scale: 0.97 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-1">Leaderboard</h3>
              <p className="text-sm opacity-90">See top typers</p>
            </div>
            <Trophy className="w-8 h-8" />
          </div>
        </motion.button>
      </motion.div>

      {/* Recent Activity */}
      {userState.isAuthenticated && (
        <motion.div 
          className="mt-8 bg-cardBg rounded-xl p-6 shadow-lg border border-secondary"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Recent Activity</h3>
            <button 
              onClick={() => navigate('/profile')}
              className="text-accent text-sm hover:underline flex items-center"
            >
              <span>View all</span>
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
          <div className="space-y-3">
            {loadingActivity && (
              <div className="text-center py-8 text-textMuted">Loading activity...</div>
            )}
            {!loadingActivity && recentActivity.length === 0 && (
              <div className="text-center py-8 text-textMuted">No recent activity yet. Start typing!</div>
            )}
            {!loadingActivity && recentActivity.length > 0 && (
              recentActivity.map((activity: UserActivity) => (
                <div key={activity.id} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                  <div>
                    <div className="font-medium">{activity.title}</div>
                    <div className="text-sm text-textMuted">{activity.time}</div>
                  </div>
                  <div className="text-right">
                    {activity.type === 'test' && (
                      <>
                        <div className="font-bold text-accent">{activity.wpm} WPM</div>
                        <div className="text-sm text-textMuted">{activity.accuracy}% ACC</div>
                      </>
                    )}
                    {activity.type === 'battle' && (
                      <>
                        <div className="font-bold text-green-500">{activity.xp ? `+${activity.xp} XP` : 'Victory'}</div>
                        <div className="text-sm text-textMuted">{activity.wpm} WPM</div>
                      </>
                    )}
                    {activity.type === 'achievement' && (
                      <div className="font-bold text-purple-500">+{activity.xp} XP</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Home;
